/**
 * Timestamp Processing Utilities
 *
 * Processes timestamps in podcast show notes, converting them to clickable
 * TimestampLink marks. The TimestampLinkElement component handles click
 * behavior (seeking the audio player) — no document-level listeners needed.
 *
 * Used by both Dream Editor preview and Orchid runtime rendering.
 */

import type { JSONContent } from '@tiptap/core';

import type { Mark } from '@/textLeaf';

const TIMESTAMP_REGEX = /(\d{1,2}:\d{2}(?::\d{2})?)/;

/**
 * Converts a timestamp string (MM:SS or HH:MM:SS) to total seconds.
 * @example
 * timestampToSeconds("1:23") => 83
 * timestampToSeconds("1:23:45") => 5025
 */
const timestampToSeconds = (timestamp: string): number => {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

interface TiptapTextNode {
  type: 'text';
  text: string;
  marks?: Mark[];
}

/**
 * Processes text nodes containing timestamps and converts them to clickable links.
 * Timestamps like "13:17" become bold, underlined TimestampLink marks that the
 * serializer renders via TimestampLinkElement (with built-in audio seeking).
 */
const processTextNodesWithTimestamps = (text: string, existingMarks?: Mark[]) => {
  const parts = text.split(TIMESTAMP_REGEX);

  // No timestamps found — preserve original marks
  if (parts.length === 1) {
    return [{ type: 'text', text, marks: existingMarks }];
  }

  const nodes: TiptapTextNode[] = [];
  for (const part of parts) {
    if (!part) continue;

    if (TIMESTAMP_REGEX.test(part)) {
      const seconds = timestampToSeconds(part);
      nodes.push({
        type: 'text',
        text: part,
        marks: [
          ...(existingMarks || []),
          { type: 'bold' },
          { type: 'underline' },
          { type: 'timestampLink', attrs: { seconds } },
        ],
      });
    } else {
      nodes.push({ type: 'text', text: part, marks: existingMarks });
    }
  }

  return nodes;
};

/**
 * Recursively processes a Tiptap document to convert timestamps in text nodes to clickable links.
 * Used for podcast show notes - timestamps like "13:17 Topic name" become clickable and seek the audio player.
 *
 * @param doc - The Tiptap document/node to process
 * @returns Processed document with timestampLink marks
 */
export const processTimestampsInTiptap = (doc: JSONContent): JSONContent => {
  if (!doc || typeof doc !== 'object') return doc;

  const processNode = (node: any): any => {
    if (!node || typeof node !== 'object') return node;

    // Process text nodes
    if (node.type === 'text' && typeof node.text === 'string') {
      const processed = processTextNodesWithTimestamps(node.text, node.marks);
      // If no timestamps found, return original node
      if (processed.length === 1 && processed[0].text === node.text) {
        return node;
      }
      // Return the processed nodes (will be flattened by parent)
      return processed;
    }

    // Process content array recursively
    if (Array.isArray(node.content)) {
      const processedContent: any[] = [];
      for (const child of node.content) {
        const processed = processNode(child);
        // Flatten arrays (from text node splitting)
        if (Array.isArray(processed)) {
          processedContent.push(...processed);
        } else {
          processedContent.push(processed);
        }
      }
      return { ...node, content: processedContent };
    }

    return node;
  };

  return processNode(doc);
};
